import React, { useRef, useState } from "react";
import { faArrowCircleUp, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const FileUpload = ({ file, onFileChange, accept, maxSizeMB = 5 }) => {
  const inputRef = useRef();
  const [errorMessage, setErrorMessage] = useState(null);

  // Function to get file icon based on file type
  const getFileIcon = (file) => {
    if (!file) return "fas fa-file";
    
    const fileName = file instanceof File ? file.name : file;
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return "fas fa-file-pdf text-danger";
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'avif':
        return "fas fa-file-image text-primary";
      case 'xlsx':
      case 'xls':
        return "fas fa-file-excel text-success";
      default:
        return "fas fa-file text-secondary";
    }
  };

  // Function to validate file type based on accept prop
  const validateFileType = (file) => {
    if (!accept) return true; // If no accept prop, allow all files
    
    const allowedExtensions = accept
      .split(',')
      .map(ext => ext.trim().replace('.', '').toLowerCase());
    
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    
    return allowedExtensions.includes(fileExtension);
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];

    // Validate file type
    if (!validateFileType(selectedFile)) {
      const allowedTypes = accept ? accept.replace(/\./g, '').toUpperCase() : 'allowed file types';
      setErrorMessage(
        `Only ${allowedTypes} files are allowed.`
      );
      return;
    }

    // Validate file size
    if (selectedFile.size > maxSizeMB * 1024 * 1024) {
      setErrorMessage(`Maximum file size allowed is ${maxSizeMB} MB.`);
      return;
    }

    setErrorMessage(null);
    onFileChange(selectedFile);
  };

  const handleDeleteFile = () => {
    onFileChange(null);
  };

  return (
    <div className="">
      {file ? (
        <div className="border rounded-2xl position-relative p-3 d-flex align-items-center gap-3">
          <div>
            <a
              href={file instanceof File ? URL.createObjectURL(file) : file}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i
                className={getFileIcon(file)}
                style={{ fontSize: "5rem" }}
              ></i>
            </a>
          </div>
          <div>
            <a
              href={file instanceof File ? URL.createObjectURL(file) : file}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none text-dark"
            >
              {file instanceof File && (
                <div className="fw-bold" style={{ fontSize: "1.1rem" }}>
                  {file.name}
                </div>
              )}
            </a>
            <div className="text-muted small">
              {file instanceof File
                ? (file.size / (1024 * 1024)).toFixed(2) + " MB"
                : "Existing File"}
            </div>
          </div>
          <div
            onClick={handleDeleteFile}
            className="position-absolute top-0 end-0 m-2 cursor-pointer"           
            style={{ top: '1px', right: '1px' }}
          >
            <FontAwesomeIcon
              icon={faTrash}
              className="text-danger"
              style={{ fontSize: '1.2rem' }}
            />
          </div>
        </div>
      ) : (
        <>
          <div
            className="d-flex align-items-center justify-content-between cursor-pointer border border-2 rounded border-dashed p-4 max-w-14rem hover-bg-secondary"
            onClick={() => inputRef.current.click()}
          >
            <FontAwesomeIcon
              icon={faArrowCircleUp}
              size="2x"
              className="text-primary"
            />
            <div className="mx-2">
              <div className="text-base font-weight-bold text-primary">
                Upload file
              </div>
              <div className="text-xs text-gray">
                {accept ? `${accept.replace(/\./g, '').toUpperCase()} files` : 'Multiple file types'} - Max {maxSizeMB} MB
              </div>
            </div>
            <input
              type="file"
              hidden
              ref={inputRef}
              onChange={handleFileChange}
              accept={accept}
            />
          </div>
          {errorMessage && (
            <p className="text-xs mt-1 text-danger">{errorMessage}</p>
          )}
        </>
      )}
    </div>
  );
};

export default FileUpload;